/* eslint-disable react-hooks/exhaustive-deps */
import clsx from 'clsx'
import {useState, useEffect, KeyboardEvent} from 'react'
import {Link} from 'react-router-dom'
import {KTSVG, toAbsoluteUrl} from '../../../helpers'
import {IHeader, useLayout} from '../../core'
import {useThemeMode} from '../../../partials/layout/theme-mode/ThemeModeProvider'
import {DefaultTitle} from './page-title/DefaultTitle'
import {Topbar} from './Topbar'

const calculateStickyOffset = (header: IHeader): string => {
  if (header.fixed.desktop && header.fixed.tabletAndMobile) {
    return '{default: "200px", lg: "300px"}'
  }

  if (header.fixed.desktop && !header.fixed.tabletAndMobile) {
    return '{lg: "300px"}'
  }

  if (header.fixed.tabletAndMobile && !header.fixed.desktop) {
    return '{default: "200px", lg: false}'
  }

  return ''
}

const calculateShowSticky = (header: IHeader): boolean => {
  return (
    (header.fixed.desktop && header.fixed.tabletAndMobile) ||
    (header.fixed.desktop && !header.fixed.tabletAndMobile) ||
    (header.fixed.tabletAndMobile && !header.fixed.desktop)
  )
}

export function HeaderWrapper() {
  const {config, classes, attributes} = useLayout()
  const {header} = config
  const {layoutWidth} = useThemeMode()
  const appTitle = process.env.REACT_APP_TITLE?.toLowerCase()
  const shouldShowAsideToggle =
    appTitle !== 'dsos' && config.aside?.display !== false && layoutWidth === 'full'
  const [stickyOffset, setStickyOffset] = useState<string>(calculateStickyOffset(header))
  const [showSticky, setShowSticky] = useState<boolean>(calculateShowSticky(header))
  const [isDesktopAsideHidden, setIsDesktopAsideHidden] = useState<boolean>(() => {
    if (typeof document === 'undefined' || layoutWidth !== 'full') {
      return false
    }

    return document.body.classList.contains('aside-hidden')
  })

  useEffect(() => {
    setStickyOffset(calculateStickyOffset(header))
    setShowSticky(calculateShowSticky(header))
  }, [header])

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }

    const {body} = document

    if (isDesktopAsideHidden && layoutWidth === 'full') {
      body.classList.add('aside-hidden')
    } else {
      body.classList.remove('aside-hidden')
    }

    return () => {
      body.classList.remove('aside-hidden')
    }
  }, [isDesktopAsideHidden, layoutWidth])

  useEffect(() => {
    if (config.aside?.display === false) {
      setIsDesktopAsideHidden(false)
    }
  }, [config.aside?.display])

  useEffect(() => {
    if (!shouldShowAsideToggle) {
      setIsDesktopAsideHidden(false)
    }
  }, [shouldShowAsideToggle])

  useEffect(() => {
    if (layoutWidth !== 'full') {
      setIsDesktopAsideHidden(false)
    }
  }, [layoutWidth])

  const handleAsideToggle = () => {
    if (typeof window !== 'undefined' && window.innerWidth >= 992 && layoutWidth === 'full') {
      setIsDesktopAsideHidden((prev) => !prev)
    }
  }

  const handleAsideToggleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      if (typeof window !== 'undefined' && window.innerWidth >= 992 && layoutWidth === 'full') {
        event.preventDefault()
        setIsDesktopAsideHidden((prev) => !prev)
      }
    }
  }

  return (
    <div
      id='kt_header'
      className={clsx('header', classes.header.join(' '))}
      {...attributes.headerMenu}
      data-kt-sticky={showSticky ? 'true' : 'false'}
      data-kt-sticky-name='header'
      data-kt-sticky-offset={stickyOffset}
    >
      <div
        className={clsx(classes.headerContainer.join(' '), 'd-flex flex-stack flex-wrap gap-2')}
        id='kt_header_container'
      >
        <DefaultTitle />

        {/* begin::Wrapper */}
        <div className='d-flex align-items-center ms-n2 me-2'>
          {/* begin::Aside mobile toggle */}
          {shouldShowAsideToggle && (
            <div
              className={clsx('btn btn-icon btn-active-icon-primary', {
                active: isDesktopAsideHidden,
              })}
              id='kt_aside_toggle'
              role='button'
              aria-pressed={isDesktopAsideHidden}
              onClick={handleAsideToggle}
              tabIndex={0}
              onKeyDown={handleAsideToggleKeyDown}
            >
              <KTSVG path='/media/icons/duotune/abstract/abs015.svg' className='svg-icon-1 mt-1' />
            </div>
          )}
          {/* end::Aside mobile toggle */}

          {/* begin::Logo */}

          <Link to='/dashboard' className='d-flex align-items-center d-lg-none'>
            <img
              alt='Logo'
              src={toAbsoluteUrl('/media/logos/gradeurexam2.png')}
              className='h-md-100px h-60px mt-5 mt-md-0'
            />
          </Link>
          {/* end::Logo */}
        </div>
        {/* end::Wrapper */}
        <Topbar />
      </div>
    </div>
  )
}
